/**
 * SessionContext - Unified Authentication and Role Management
 *
 * This context provides a unified, atomic approach to managing user authentication
 * and role-based permissions, eliminating the race condition that occurred with
 * separate AuthContext and RoleContext implementations.
 *
 * CRITICAL FIXES IMPLEMENTED:
 *
 * FIX #1: Unified Context Architecture (ARCHITECTURAL)
 * - Merged auth and role into single context to prevent race conditions
 * - Single loading state ensures atomic user + role updates
 * - No more separate context coordination required
 *
 * FIX #2: Fetch Cancellation (HIGH PRIORITY - Race Condition Prevention)
 * - Added isCancelled flag to prevent stale updates from previous user
 * - Prevents User A's role from overwriting User B's role on rapid switching
 * - Cleanup function sets flag before unsubscribing from Firestore listener
 *
 * FIX #3: Real-time Role Updates (HIGH PRIORITY - Security)
 * - Uses onSnapshot instead of one-time fetch for automatic role updates
 * - Eliminates need for periodic refresh interval (better than polling)
 * - Role changes in Firestore immediately reflected in frontend
 * - Critical for security: revoked permissions take effect immediately
 *
 * FIX #4: Error State and User Feedback (MEDIUM PRIORITY - UX)
 * - Exposes error state through context for UI feedback
 * - Network errors no longer silently fail with default role
 * - Missing user documents properly reported
 * - Consumers can display error messages to users
 *
 * FIX #5: Strengthened Role Override Security (MEDIUM PRIORITY - Security)
 * - Multiple environment checks (import.meta.env.MODE + DEV flag)
 * - Explicit security logging for production attempts
 * - Clear console feedback for development usage
 * - Super Admin requirement enforced
 *
 * SECURITY FEATURES:
 * - Fail-safe design: errors default to no permissions (null role)
 * - No default role assignment on error (prevents privilege escalation)
 * - Real-time role revocation (onSnapshot listener)
 * - Development-only role override with strict checks
 *
 * EDGE CASES HANDLED:
 * - User logout: Immediate state cleanup, no lingering permissions
 * - User switching: Cancellation flag prevents stale data
 * - Network errors: Error state exposed, no silent failures
 * - Rapid navigation: Memoized values prevent unnecessary re-renders
 * - Tab switching: Firebase re-validates token, role listener stays active
 * - Role changes: Real-time updates via onSnapshot
 * - React Strict Mode: Pure component design, no side effects in render
 *
 * @see Technical Review: /tmp/auth_race_condition_technical_review.md
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { User } from 'firebase/auth'
import { db, auth, initializeAuth } from '../firebase'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { onAuthStateChanged, Unsubscribe } from 'firebase/auth'
import { UserRoles, type UserRole } from '../constants/userRoles'
import { syncAccountEmail } from '../services/accountEmail'
import {
  extractCustomClaims,
  clearCachedClaims,
  type CustomClaims,
} from '../services/auth/claims.service'
import { logger } from '../services/logger'
import { rememberRefusedEmail } from '../services/auth/inviteOnly'
import { securityService } from '../services/security'

// --- Role hierarchy for permission checking ---

const roleHierarchy: Record<UserRole, number> = {
  [UserRoles.SUPER_ADMIN]: 4,
  [UserRoles.CORPORATE]: 3,
  [UserRoles.ADMIN]: 2,
  [UserRoles.MANAGEMENT]: 1,
  [UserRoles.TENANT]: 0,
}

// --- Define the new, unified context interface ---

interface SessionContextType {
  currentUser: User | null
  userRole: UserRole | null
  actualUserRole: UserRole | null
  tokenRole: UserRole | null
  tokenClaims: CustomClaims | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  isInitialized: boolean
  hasRole: (requiredRole: UserRole) => boolean
  refreshToken: () => Promise<void>
  refreshUser: () => void
  /** First + last name from the user's record; a password sign-up has no displayName (PB-58) */
  recordName: string | null
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useSession = () => {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}

// --- The new, unified SessionProvider ---

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userVersion, setUserVersion] = useState(0)
  const [actualUserRole, setActualUserRole] = useState<UserRole | null>(null)
  const [recordName, setRecordName] = useState<string | null>(null)
  const [tokenRole, setTokenRole] = useState<UserRole | null>(null)
  const [tokenClaims, setTokenClaims] = useState<CustomClaims | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUserId, setLastUserId] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // CRITICAL FIX: Detect when currentUser changes and set loading synchronously
  // This prevents race condition on hard refresh where ProtectedRoute evaluates
  // before role is fetched from Firestore. This code runs synchronously during
  // render (before useEffect), ensuring loading state is immediately updated.
  const currentUserId = currentUser?.uid || null
  if (currentUserId !== lastUserId) {
    // User changed - set loading immediately (synchronously)
    if (currentUserId) {
      // User logged in or switched - start loading
      setLoading(true)
    }
    setLastUserId(currentUserId)
  }

  useEffect(() => {
    let authUnsubscribe: Unsubscribe | undefined

    // CRITICAL FIX: Await auth persistence initialization before setting up listener
    // This ensures onAuthStateChanged fires AFTER localStorage auth is restored,
    // preventing the race condition where user appears as null on hard refresh
    const initAuthListener = async () => {
      try {
        // Wait for Firebase to restore auth from localStorage
        await initializeAuth()
        logger.info(
          'Auth persistence ready, setting up listener',
          {},
          'SessionContext'
        )

        // Now subscribe to auth state changes - user will be restored from localStorage
        authUnsubscribe = onAuthStateChanged(auth, user => {
          logger.debug(
            'Auth state changed',
            { hasUser: !!user, uid: user?.uid },
            'SessionContext'
          )
          console.info('[AuthRestore] auth state', {
            hasUser: !!user,
            uid: user?.uid,
            mfa: user ? 'multiFactor' in user : undefined,
            ms: Math.round(performance.now()),
          })
          setCurrentUser(user)
          setIsInitialized(true) // Mark as initialized after first auth state check

          if (!user) {
            // User is logged out
            setActualUserRole(null)
            setRecordName(null)
            setTokenRole(null)
            setTokenClaims(null)
            setLoading(false)
            setError(null)
            // Clear cached claims on logout
            clearCachedClaims()
          } else {
            // CRITICAL FIX: Keep loading true when user exists but role not yet loaded
            // This prevents race condition on hard refresh where ProtectedRoute
            // evaluates before role is fetched from Firestore
            setLoading(true)
          }
          // The role listener effect below will set loading to false when role is fetched.
        })
      } catch (error) {
        logger.error('Failed to initialize auth', error, {
          context: 'SessionContext',
        })
        // On error, still set loading to false so app doesn't hang
        setIsInitialized(true)
        setLoading(false)
      }
    }

    initAuthListener()

    return () => {
      if (authUnsubscribe) {
        authUnsubscribe()
      }
    }
  }, [])

  const emailSyncAttempted = useRef(false)
  useEffect(() => {
    let roleUnsubscribe: Unsubscribe | undefined
    // FIX #2: Add cancellation flag to prevent stale updates from previous user's fetch
    let isCancelled = false

    if (currentUser) {
      setLoading(true)
      const userDocRef = doc(db, 'users', currentUser.uid)

      // Real-time listener for role changes (better than periodic refresh)
      // This automatically handles role updates when they change in Firestore
      roleUnsubscribe = onSnapshot(
        userDocRef,
        doc => {
          // FIX #2: Check cancellation flag before updating state
          if (isCancelled) return

          if (doc.exists()) {
            const userData = doc.data()
            // A deleted person is treated like someone with no account
            // (PB-74): sign them out, live, even mid-session. The sign-in
            // page then shows the no-invitation notice.
            if (userData.accountStatus === 'deleted') {
              logger.info(
                '[Session] record deleted, signing out',
                {},
                'SessionContext'
              )
              rememberRefusedEmail(currentUser.email ?? '')
              console.info('[AuthRestore] signOut: record deleted')
              auth.signOut()
              return
            }
            setActualUserRole(userData.role as UserRole)
            setRecordName(
              [userData.firstName, userData.lastName]
                .filter(Boolean)
                .join(' ') || null
            )
            setError(null)

            // Sync emailVerified from Firebase Auth → Firestore
            if (currentUser.emailVerified && userData.emailVerified === false) {
              updateDoc(doc.ref, { emailVerified: true }).catch(err =>
                console.warn('Failed to sync emailVerified:', err)
              )
            }

            // A confirmed email change (PB-63): the sign-in email moved, the
            // record didn't. Users can't write their own email, so the server
            // copies it. Once per session.
            if (
              currentUser.email &&
              currentUser.emailVerified &&
              userData.email !== currentUser.email &&
              !emailSyncAttempted.current
            ) {
              emailSyncAttempted.current = true
              syncAccountEmail().catch(err =>
                console.warn('[EmailSync] failed:', err)
              )
            }
          } else {
            // SECURITY FIX: User document not found. Do not assign a default role.
            // Treat as a non-permissioned user.
            setActualUserRole(null)
            setError('User profile does not exist.')
          }
          setLoading(false)
        },
        err => {
          // FIX #2: Check cancellation flag before updating state
          if (isCancelled) return

          logger.error('Error fetching user role', err, {
            context: 'SessionContext',
          })
          // SECURITY FIX: Error fetching role. Do not assign a default role.
          setActualUserRole(null)
          setError('Failed to load user permissions.')
          setLoading(false)
        }
      )
    } else {
      // No user, ensure everything is cleared
      setActualUserRole(null)
      setLoading(false)
    }

    // Cleanup the Firestore listener on unmount or when user changes
    return () => {
      // FIX #2: Set cancellation flag to prevent stale updates
      isCancelled = true
      if (roleUnsubscribe) {
        roleUnsubscribe()
      }
    }
  }, [currentUser])

  // ====================
  // TOKEN VERIFICATION EFFECT
  // ====================
  // Verify ID token custom claims for tamper-proof role verification
  useEffect(() => {
    if (!currentUser || !actualUserRole) {
      setTokenRole(null)
      setTokenClaims(null)
      return
    }

    const verifyToken = async () => {
      try {
        logger.debug(
          'Verifying ID token claims',
          { uid: currentUser.uid },
          'SessionContext'
        )
        const claims = await extractCustomClaims(currentUser, false)

        if (!claims) {
          logger.debug(
            'No custom claims found in token',
            { uid: currentUser.uid },
            'SessionContext'
          )
          setTokenRole(null)
          setTokenClaims(null)
          return
        }

        // SECURITY: Verify token role matches Firestore role
        if (claims.role !== actualUserRole) {
          securityService.logRoleMismatch(
            currentUser.uid,
            claims.role || 'none',
            actualUserRole || 'none'
          )

          // Force token refresh to get latest claims
          logger.info(
            'Forcing token refresh to resolve mismatch',
            { uid: currentUser.uid },
            'SessionContext'
          )
          const refreshedClaims = await extractCustomClaims(currentUser, true)

          if (refreshedClaims && refreshedClaims.role !== actualUserRole) {
            securityService.logRoleMismatch(
              currentUser.uid,
              refreshedClaims.role || 'none',
              actualUserRole || 'none'
            )
            logger.warn(
              'Role mismatch persists after refresh',
              {
                uid: currentUser.uid,
                tokenRole: refreshedClaims.role,
                firestoreRole: actualUserRole,
              },
              'SessionContext'
            )
            // TODO: Consider force logout or alert user
            // For now, we trust Firestore role but log the security concern
          }

          setTokenRole(refreshedClaims?.role || null)
          setTokenClaims(refreshedClaims)
        } else {
          logger.debug(
            'Token role verified successfully',
            {
              role: claims.role,
              uid: currentUser.uid,
            },
            'SessionContext'
          )
          setTokenRole(claims.role)
          setTokenClaims(claims)
        }
      } catch (error) {
        logger.error('Token verification failed', error, {
          uid: currentUser.uid,
          context: 'SessionContext',
        })
        securityService.logTokenError(currentUser.uid, error)
        setTokenRole(null)
        setTokenClaims(null)
      }
    }

    verifyToken()

    // Re-verify on role change
  }, [currentUser, actualUserRole])

  // ====================
  // TOKEN REFRESH MONITORING
  // ====================
  // Proactively refresh tokens before expiry to prevent auth interruptions
  useEffect(() => {
    if (!currentUser) return

    const monitorToken = setInterval(async () => {
      try {
        const tokenResult = await currentUser.getIdTokenResult(false)
        const expirationTime = new Date(tokenResult.expirationTime)
        const timeUntilExpiry = expirationTime.getTime() - Date.now()

        // Refresh 5 minutes before expiry
        if (timeUntilExpiry < 5 * 60 * 1000) {
          logger.info(
            'Proactively refreshing token',
            {
              uid: currentUser.uid,
              timeUntilExpiry: Math.round(timeUntilExpiry / 1000),
            },
            'SessionContext'
          )
          securityService.logTokenRefresh(currentUser.uid, 'proactive')

          await currentUser.getIdToken(true)

          // Re-extract claims after refresh
          const refreshedClaims = await extractCustomClaims(currentUser, true)
          if (refreshedClaims) {
            setTokenRole(refreshedClaims.role)
            setTokenClaims(refreshedClaims)
          }
        }
      } catch (error) {
        logger.error('Token monitoring failed', error, {
          uid: currentUser.uid,
          context: 'SessionContext',
        })
        securityService.logTokenError(currentUser.uid, error)
      }
    }, 60 * 1000) // Check every minute

    return () => clearInterval(monitorToken)
  }, [currentUser])

  const userRole = actualUserRole

  const hasRole = useCallback(
    (requiredRole: UserRole) => {
      if (!userRole) return false
      return roleHierarchy[userRole] >= roleHierarchy[requiredRole]
    },
    [userRole]
  )

  const handleRefreshToken = useCallback(async () => {
    if (!currentUser) {
      logger.warn(
        'Cannot refresh token: No user logged in',
        {},
        'SessionContext'
      )
      return
    }

    try {
      logger.info(
        'Manually refreshing token',
        { uid: currentUser.uid },
        'SessionContext'
      )
      securityService.logTokenRefresh(currentUser.uid, 'manual')

      await currentUser.getIdToken(true)

      // Re-extract claims after refresh
      const refreshedClaims = await extractCustomClaims(currentUser, true)
      if (refreshedClaims) {
        setTokenRole(refreshedClaims.role)
        setTokenClaims(refreshedClaims)
        logger.info(
          'Token refreshed successfully',
          {
            uid: currentUser.uid,
            role: refreshedClaims.role,
          },
          'SessionContext'
        )
      }
    } catch (error) {
      logger.error('Manual token refresh failed', error, {
        uid: currentUser.uid,
        context: 'SessionContext',
      })
      securityService.logTokenError(currentUser.uid, error)
    }
  }, [currentUser])

  const value = useMemo(
    () => ({
      currentUser,
      userRole,
      actualUserRole,
      tokenRole,
      tokenClaims,
      loading,
      error,
      isAuthenticated: !!currentUser && !loading && !error,
      isInitialized,
      hasRole,
      refreshToken: handleRefreshToken,
      refreshUser: () => {
        // Force re-render after Firebase Auth profile updates (e.g., displayName)
        // The User object is mutated in place by updateProfile, so we bump a
        // version counter to trigger consumers to re-read currentUser properties
        setUserVersion(v => v + 1)
      },
      recordName,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userVersion triggers re-render after profile updates
    [
      currentUser,
      userRole,
      actualUserRole,
      tokenRole,
      tokenClaims,
      loading,
      error,
      isInitialized,
      hasRole,
      handleRefreshToken,
      userVersion,
      recordName,
    ]
  )

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}
